// sanguine-damage-type-pick.ts — the ONE client mirror of the Sanguine Elemental/Transmuted
// damage-type pick (`next_spell_damage_type_choice`, Tier 4 of the sanguine sweep).
//
// A STATUS on the caster carrying `next_spell_damage_type_choice` lets them pick the damage type of
// their next damaging SPELL. The allowed set is the carrier item's own multi `damage_type` entries
// (Elemental authors Cold/Fire/Lightning; Transmuted authors NONE = any type). The pick rides
// `use_ability_variable_2`; the SERVER re-authorizes it in janus() (ft_sanguine_next_spell_damage_
// type_pick_allowed), so everything here is display sugar — it can never widen what's legal.
//
// This lives outside action-bar.ts because THREE surfaces cast a spell and all three must offer the
// pick (the 2026-07-22 build wired only the first, which is why the buff looked dead in play):
//   • the action-bar choicebar         (tabs/action-bar.ts)
//   • the PC-sheet spell table row     (tabs/castable-spells.ts + ability-button.ts)
//   • the FT3D AOE placement confirm   (encounter-map/aoe/FT3DAOEPlacement.ts, reads the DOM select)
// Keep the predicate here and only here — a second copy is how the surfaces drift.

/** Server mirror: ft_sanguine_next_spell_damage_type_pick_allowed() refuses slow casts (the finish
 *  phase re-resolves after this turn-scoped buff expired). Same list as action-bar's QR_SLOW_CAST_TIMES. */
const SLOW_CAST_TIMES = new Set([
    'one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours',
]);

/** The canonical DOM id every surface renders the pick under — the SAME id the legacy multi-type
 *  Elemental-Attack select uses, so the existing use_ability_variable_2 plumbing reads it unchanged. */
export function sanguineDmgSelectId(abilityId: string): string {
    return `input_${abilityId}_user_choice_1_damage_type`;
}

export interface SanguineDmgBuff { armed: boolean; anyType: boolean; allowed: string[] }

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' ? value as Record<string, unknown> : null;
}

/** UUID out of a layer entry — bare-UUID string, {items:{item_id}} or {item_id}. */
function entryUuid(e: unknown): string {
    if (typeof e === 'string') return e;
    const viaReader = typeof ftItemsCol === 'function' ? ftItemsCol(e, 'item_id') : undefined;
    return String(viaReader ?? recordOf(e)?.item_id ?? '');
}

/** All live caster statuses carrying next_spell_damage_type_choice — client mirror of
 *  ft_sanguine_next_spell_damage_type_choice_carriers(). anyType (Transmuted) wins over a set. */
export function casterDamageTypeChoiceBuff(caster: unknown): SanguineDmgBuff {
    if (!caster || typeof ftCreaturePayloadStatuses !== 'function') return { armed: false, anyType: false, allowed: [] };
    let statuses: unknown[];
    try { statuses = ftCreaturePayloadStatuses(caster) || []; } catch { statuses = []; }
    if (!Array.isArray(statuses)) return { armed: false, anyType: false, allowed: [] };
    const allowed = new Set<string>();
    let armed = false;
    let anyType = false;
    for (const st of statuses) {
        const status = recordOf(st);
        const def = recordOf(status?.status_arr) ?? st;
        if (!ftLayerHas(def, 'next_spell_damage_type_choice')) continue;
        if (!Number(ftLayerValue(def, 'next_spell_damage_type_choice'))) continue;
        armed = true;
        const entries = ftLayerEntries(def, 'damage_type');
        if (entries.length === 0) { anyType = true; continue; }   // Transmuted: any type
        for (const e of entries) { const uid = entryUuid(e); if (uid) allowed.add(uid.toUpperCase()); }
    }
    return { armed, anyType, allowed: anyType ? [] : [...allowed] };
}

/** Every damage-type UUID in the game — the option list for Transmuted (empty allowed set).
 *  DAMAGETYPE_CATALOG is emitted by ft_boot_inc.php on every page; DAMAGE_TYPES (template.js) is the
 *  fallback. Returns [] rather than a WRONG list when neither is present. */
declare const DAMAGE_TYPES: Record<string, { id: string; [k: string]: unknown }> | undefined;
function allDamageTypeIds(): string[] {
    // DAMAGETYPE_CATALOG is DOUBLE-KEYED (each type appears under both its variable_name and its
    // UUID), so the raw Object.values() walk yields every type twice — dedupe or Transmuted offers
    // a 26-option list of 13 types.
    const cat = window.DAMAGETYPE_CATALOG;
    if (cat && typeof cat === 'object') {
        const ids = [...new Set(Object.values(cat).map((d) => String(d?.item_id ?? '').toUpperCase()).filter(Boolean))];
        if (ids.length > 0) return ids;
    }
    if (typeof DAMAGE_TYPES !== 'undefined' && DAMAGE_TYPES) {
        return [...new Set(Object.values(DAMAGE_TYPES).map((d) => String(d.id).toUpperCase()).filter(Boolean))];
    }
    return [];
}

/** UUID → display name, from the same catalog the spell table's own damage selects use. */
export function sanguineDamageTypeName(id: string): string {
    const cat = window.DAMAGETYPE_CATALOG;
    if (cat && typeof cat === 'object') {
        for (const k of Object.keys(cat)) {
            if (cat[k]?.item_id === id) return String(cat[k]?.item_name || 'Damage Type');
        }
    }
    const viaLookup = typeof ftItemNameById === 'function' ? String(ftItemNameById(id) ?? '') : '';
    return viaLookup || 'Damage Type';
}

/**
 * The damage types to OFFER for this cast — [] means render no picker.
 *
 * Client mirror of the server predicate: a damaging SPELL (dice+die), fast casting time, whose own
 * authored damage_type list has ≤1 entry (2+ = the legacy Elemental-Attack picker already owns
 * variable_2), cast by a caster with an armed carrier. `itemSourced` casts are excluded because a
 * magical-item / consumable cast already spends variable_2 on its source row (Codex review
 * 2026-07-22 finding 4); the dedicated cast-source channel is a filed follow-up.
 */
export function sanguineDmgPickOptionIds(caster: unknown, ability: unknown, itemSourced: boolean): string[] {
    if (!ability || !caster || itemSourced) return [];
    if (String(ftItemsCol(ability, 'item_type') ?? '') !== 'spell') return [];
    if (SLOW_CAST_TIMES.has(String(ftLayerValue(ability, 'spell_casting_time') ?? ''))) return [];
    const body = instantProjectileBody(ability) ?? ability;
    if (!ftLayerHas(body, 'dice') || !ftLayerHas(body, 'die')) return [];   // not a damaging spell
    if (ftLayerEntries(body, 'damage_type').length > 1) return [];             // legacy multi-type flow owns var-2
    const buff = casterDamageTypeChoiceBuff(caster);
    if (!buff.armed) return [];
    return buff.anyType ? allDamageTypeIds() : buff.allowed;
}

/** An INSTANT PROJECTILE VOLLEY parent (Scorching Ray, Magic Missile: spell_upcast_additional_projectiles_created + one free_ability
 *  child) damages through its projectile, so the pick is judged on the child — exactly as the engine judges the parent's plan against its
 *  projectile (prepareNextSpell) and retypes each projectile's damage from it. null = not a volley parent (judge the spell itself). */
function instantProjectileBody(ability: unknown): unknown {
    if (!ftLayerHas(ability, 'spell_upcast_additional_projectiles_created') || ftLayerHas(ability, 'dice')) return null;
    const children = ftLayerEntries(ability, 'free_ability');
    if (children.length !== 1) return null;
    const nested = recordOf(children[0]);
    if (nested && (nested.item_layer_types || nested.layers)) return nested;
    const id = entryUuid(children[0]).toUpperCase();
    return id ? (window.ftFindFullNestedItem?.(id) ?? null) : null;
}

/** Convenience predicate for callers that only need "will a picker render?". */
export function sanguineDmgPickEligible(caster: unknown, ability: unknown, itemSourced: boolean): boolean {
    return sanguineDmgPickOptionIds(caster, ability, itemSourced).length > 0;
}
